/**
 * Protected Route Component
 * Wraps routes that require authentication or admin role
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/hooks';
import { useEchoUser } from '../hooks/useEchoUser';
import { Card, Stack, Button, Flex, useTheme, spacing, fontSize } from '@fti/design-system';

export interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAdmin?: boolean;
}

/**
 * Component that protects routes requiring authentication
 * Optionally requires admin role
 */
export function ProtectedRoute({
  children,
  requireAdmin = false,
}: ProtectedRouteProps) {
  const auth = useAuth();
  const { user, isLoading, isAdmin, error } = useEchoUser();
  const location = useLocation();
  const { theme } = useTheme();

  // Show loading state while checking auth (but not for Echo user - that can fail)
  if (auth.isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '200px',
          color: theme.colors.text.secondary,
        }}
      >
        Checking authentication...
      </div>
    );
  }

  // Redirect to sign in if not authenticated
  if (!auth.isAuthenticated) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  // Show error if user fetch failed - but allow continuing (backend may be down)
  if (error && !isLoading) {
    return (
      <Card variant="elevated" style={{ maxWidth: '500px', margin: '0 auto' }}>
        <Card.Body>
          <Stack gap="md">
            <h2 style={{ color: theme.colors.semantic.warning.default }}>
              Backend Connection Issue
            </h2>
            <p style={{ color: theme.colors.text.secondary }}>
              Could not connect to the Echo API. The backend may be starting up or unavailable.
            </p>
            <p style={{ color: theme.colors.text.secondary, fontSize: fontSize.sm }}>
              Error: {error.message}
            </p>
            <Flex gap="sm">
              <Button
                variant="solid"
                color="primary"
                onClick={() => window.location.reload()}
              >
                Retry
              </Button>
              <Button
                variant="outline"
                color="primary"
                onClick={() => window.history.back()}
              >
                Go Back
              </Button>
            </Flex>
          </Stack>
        </Card.Body>
      </Card>
    );
  }

  // Show brief loading for Echo user (with timeout protection)
  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '200px',
          color: theme.colors.text.secondary,
        }}
      >
        Loading user data...
      </div>
    );
  }

  // Check admin requirement
  if (requireAdmin && !isAdmin) {
    return (
      <Card variant="elevated" style={{ maxWidth: '400px', margin: '0 auto' }}>
        <Card.Body>
          <Stack gap="md">
            <h2 style={{ color: theme.colors.semantic.warning.default }}>
              Access Denied
            </h2>
            <p style={{ color: theme.colors.text.secondary }}>
              This page requires administrator privileges.
            </p>
            <p
              style={{
                color: theme.colors.text.secondary,
                fontSize: fontSize.sm,
              }}
            >
              Logged in as: {user?.email} ({user?.role})
            </p>
            <Button
              variant="outline"
              color="primary"
              onClick={() => window.history.back()}
            >
              Go Back
            </Button>
          </Stack>
        </Card.Body>
      </Card>
    );
  }

  return <>{children}</>;
}

export default ProtectedRoute;
